'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import { trackSearchSuggestionClick } from '@/lib/analytics';

interface SuggestionTemplate {
  id: string;
  name: string;
  platform: string;
  category: string | null;
  price: number;
  thumbnail_url: string | null;
  tags: string[];
}

interface SearchAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  templates: SuggestionTemplate[];
  placeholder?: string;
  className?: string;
  style?: React.CSSProperties;
}

const platformColors: Record<string, string> = {
  shopify: '#96BF48',
  wordpress: '#21759B',
  woocommerce: '#7F54B3',
  html: '#E8633A',
  nextjs: '#000000',
  webflow: '#4353FF',
};

export default function SearchAutocomplete({
  value,
  onChange,
  templates,
  placeholder = 'Search templates...',
  className = '',
  style,
}: SearchAutocompleteProps) {
  const [open, setOpen] = useState(false);
  const [suggestions, setSuggestions] = useState<SuggestionTemplate[]>([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const computeSuggestions = useCallback(
    (query: string) => {
      if (!query || query.trim().length < 1) {
        setSuggestions([]);
        setOpen(false);
        return;
      }

      const q = query.toLowerCase();
      const matched = templates
        .filter((t) => {
          const nameMatch = t.name.toLowerCase().includes(q);
          const platformMatch = t.platform.toLowerCase().includes(q);
          const categoryMatch = (t.category || '').toLowerCase().includes(q);
          const tagMatch = (t.tags || []).some((tag) => tag.toLowerCase().includes(q));
          return nameMatch || platformMatch || categoryMatch || tagMatch;
        })
        .slice(0, 8);

      setSuggestions(matched);
      setOpen(matched.length > 0);
      setActiveIndex(-1);
    },
    [templates]
  );

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => computeSuggestions(value), 150);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [value, computeSuggestions]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open || suggestions.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((prev) => Math.min(prev + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((prev) => Math.max(prev - 1, -1));
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      const selected = suggestions[activeIndex];
      onChange(selected.name);
      setOpen(false);
      trackSearchSuggestionClick(selected.id, selected.name, value);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const handleSuggestionClick = (template: SuggestionTemplate) => {
    onChange(template.name);
    setOpen(false);
    trackSearchSuggestionClick(template.id, template.name, value);
  };

  return (
    <div ref={containerRef} className={`relative ${className}`} style={style}>
      <div className="relative">
        <Icon
          name="MagnifyingGlassIcon"
          size={16}
          className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
          style={{ color: 'var(--text-muted)' } as React.CSSProperties}
        />
        <input
          ref={inputRef}
          type="text"
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => value.trim().length >= 1 && suggestions.length > 0 && setOpen(true)}
          onKeyDown={handleKeyDown}
          className="w-full pl-9 pr-4 py-2.5 rounded-xl text-sm"
          style={{
            background: 'var(--bg-card)',
            border: '1px solid var(--border)',
            color: 'var(--text-primary)',
          }}
          autoComplete="off"
          aria-autoComplete="list"
          aria-expanded={open}
          aria-haspopup="listbox"
        />
        {value && (
          <button
            onClick={() => { onChange(''); setOpen(false); }}
            className="absolute right-3 top-1/2 -translate-y-1/2"
            style={{ color: 'var(--text-muted)' }}
            aria-label="Clear search"
          >
            <Icon name="XMarkIcon" size={14} />
          </button>
        )}
      </div>

      {/* Dropdown */}
      {open && suggestions.length > 0 && (
        <div
          className="absolute top-full left-0 right-0 mt-1.5 rounded-xl overflow-hidden z-50 shadow-xl"
          style={{
            background: 'var(--bg-card)',
            border: '1px solid var(--border)',
            maxHeight: '360px',
            overflowY: 'auto',
          }}
          role="listbox"
        >
          <div className="px-3 py-2 border-b" style={{ borderColor: 'var(--border)' }}>
            <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
              {suggestions.length} suggestion{suggestions.length !== 1 ? 's' : ''}
            </span>
          </div>
          {suggestions.map((template, index) => {
            const platformColor = platformColors[template.platform.toLowerCase()] || '#6B7280';
            const isActive = index === activeIndex;
            return (
              <Link
                key={template.id}
                href={`/template-detail?id=${template.id}`}
                onClick={() => handleSuggestionClick(template)}
                className="flex items-center gap-3 px-3 py-2.5 transition-colors"
                style={{
                  background: isActive ? 'var(--accent-light)' : 'transparent',
                  borderBottom: '1px solid var(--border)',
                }}
                role="option"
                aria-selected={isActive}
              >
                {/* Thumbnail */}
                <div
                  className="w-12 h-9 rounded-lg overflow-hidden flex-shrink-0"
                  style={{ background: 'var(--bg-secondary)' }}
                >
                  <AppImage
                    src={template.thumbnail_url || '/assets/images/no_image.png'}
                    alt={`${template.name} thumbnail`}
                    width={48}
                    height={36}
                    className="object-cover w-full h-full"
                  />
                </div>
                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate" style={{ color: 'var(--text-primary)' }}>
                    {template.name}
                  </p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span
                      className="text-2xs font-semibold px-1.5 py-0.5 rounded"
                      style={{ background: `${platformColor}18`, color: platformColor }}
                    >
                      {template.platform.charAt(0).toUpperCase() + template.platform.slice(1)}
                    </span>
                    {template.category && (
                      <span className="text-2xs" style={{ color: 'var(--text-muted)' }}>
                        {template.category}
                      </span>
                    )}
                  </div>
                </div>
                {/* Price */}
                <span className="text-sm font-semibold flex-shrink-0" style={{ color: 'var(--accent)' }}>
                  ${template.price}
                </span>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
